import { lazy, type ComponentType } from "react";

const RELOAD_KEY = "cana:chunk-reload-at";

/** A failure this soon after an auto-reload is not a stale chunk — the reload
 *  already replaced those. It is a real error, and reloading again would loop. */
const MIN_GAP_MS = 10_000;

function lastReloadAt(): number {
	try {
		return Number(window.sessionStorage.getItem(RELOAD_KEY)) || 0;
	} catch {
		// sessionStorage can be blocked (private mode, embedded webviews). Report
		// "reloaded just now" so we fall back to today's behaviour rather than
		// risking a reload loop we have no way to remember we are in.
		return Date.now();
	}
}

function markReload(): void {
	try {
		window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
	} catch {
		// Nothing to do — without storage, lastReloadAt() already refuses to reload.
	}
}

/**
 * `React.lazy`, except a chunk that fails to download reloads the page once
 * instead of leaving a blank screen.
 *
 * A deploy writes new hashed chunks to hosting and deletes the old ones. A tab
 * opened before that deploy is still running the old bundle, so the moment it
 * navigates to a page it has not loaded yet it asks for a file that no longer
 * exists. The import rejects and, with no error boundary above it, the user is
 * left staring at nothing. Reloading fetches index.html — which is served
 * no-cache — and with it the current chunk names.
 *
 * This runs for everyone, not just admins: the version banner asks a person to
 * act, but a patient who hits a deleted chunk has no idea what happened and no
 * reason to think a refresh would help.
 */
// The `any` mirrors React's own `lazy` signature — narrowing it here rejects
// every component that takes props.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithReload<T extends ComponentType<any>>(
	factory: () => Promise<{ default: T }>
) {
	return lazy(async () => {
		try {
			return await factory();
		} catch (error) {
			if (Date.now() - lastReloadAt() < MIN_GAP_MS) throw error;

			markReload();
			window.location.reload();

			// The page is on its way out. Resolving or rejecting here would flash
			// an error or half a screen in the moment before it goes.
			return new Promise<never>(() => {});
		}
	});
}
